import React from 'react';
import { Box, CardBody, Typography } from '@saviynt/design-system';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import type { RoleCondition } from '../../../../services';
import type { RootState } from '../../../../services/types';

import { useInvertedLocalize } from '../../../../utils/localization';
import generateRoleDescription from '../utils/generateRoleDescription';

import invertedMessages from './messages';

import './RoleCardDescription.css';

interface RoleCardDescriptionProps {
  conditions: RoleCondition[];
}

/**
 * RoleCardDescription component displaying human-readable role description
 * generated from role conditions using ICU-compliant localization
 *
 * LOCALIZATION APPROACH:
 * ======================
 * Uses industry best practices for i18n:
 * - Intl.ListFormat API for locale-aware list formatting
 * - InvertedMessages pattern for maintainable translations
 * - Complete sentence templates (not fragments)
 * - Only 3 message keys needed for infinite combinations
 *
 * Example outputs:
 * - "This role is for users whose city is Chennai."
 * - "This role is for users whose city is Chennai or Bangalore and role is Manager."
 * - "This role is for users whose city is Chennai, Bangalore, or Mumbai and department is HR."
 *
 * @param props - The component props
 * @returns JSX element containing the role description
 */
function RoleCardDescription({ conditions }: RoleCardDescriptionProps) {
  // get react-intl instance for locale info and formatting
  const intl = useIntl();

  // get localization function configured with our inverted messages
  // this provides the l() function that maps English defaults to translation keys
  const l = useInvertedLocalize(invertedMessages);

  // get user's locale from Redux for proper grammar rules in generated descriptions
  // falls back to 'en' if not set - this drives Intl.ListFormat behavior
  const userLocale = useSelector(
    (state: RootState) => state.user.userInfo?.userSavRoleMap?.locale ?? 'en'
  );

  // generate the localized description using the utility function
  // this handles all the complex logic of formatting lists and joining conditions
  // type assertion needed because l() is typed with specific message keys
  // returns ReactNode when boldAttributesAndValues is true (with bold formatting)
  const description = generateRoleDescription(
    conditions,
    l as (defaultMessage: string, placeholders?: object) => string,
    intl,
    {
      boldAttributesAndValues: true,
      locale: userLocale,
    }
  ) as string;

  // don't render anything if there are no conditions
  if (!description) {
    return null;
  }

  return (
    <CardBody className='Roles-RoleCardDescription'>
      <Box className='Roles-RoleCardDescription-content'>
        <Typography
          kind='body2'
          lineLimit={3}
          tooltipConfigs={{
            text: description,
            placement: 'top',
          }}
          className='Roles-RoleCardDescription-text'>
          {description}
        </Typography>
      </Box>
    </CardBody>
  );
}

export default RoleCardDescription;
